import { isDefined, isEmpty } from '@nfctron/nexus-core';

import { createLogger } from '@root/config/src/lib/utils/logger';

/**
 * Consent state enum for Sklik tracking
 */
export enum SklikConsentState {
	/** No consent given */
	NO_CONSENT = 0,
	/** Consent given */
	CONSENT = 1,
}

/**
 * Address information for Sklik identity tracking
 */
export interface SklikAddressInfo {
	/** Country */
	a1?: string;
	/** City */
	a2?: string;
	/** Street */
	a3?: string;
	/** Street number */
	a4?: string;
	/** Postal code */
	a5?: string;
}

/**
 * Identity data for Sklik tracking
 */
export interface SklikIdentityData {
	/** Email or hashed email */
	eid?: string;
	/** Address information */
	aid?: SklikAddressInfo;
	/** Phone number */
	tid?: string;
}

/**
 * Declare global window interface with rc property
 */
declare global {
	interface Window {
		rc?: {
			/** Sklik conversion tracking method */
			conversionHit: (conf: { id: string; value: number; consent?: SklikConsentState }) => void;
			/** Sklik retargeting tracking method */
			retargetingHit: (conf: {
				rtgId: string;
				consent?: SklikConsentState;
				item_id?: string;
				category?: string;
				pageType?: string;
				rtgUrl?: string;
			}) => void;
		};
		sznIVA?: {
			IS?: {
				/** Sklik identity tracking method */
				updateIdentities: (identities: SklikIdentityData) => void;
			};
		};
	}
}

/**
 * Conversion event types for Sklik
 * Currently only purchase events are supported
 * @see https://napoveda.sklik.cz/merici-skripty/konverzni-kod/#konverzni-kod
 */
export type SklikConversionEvent = 'purchase';

/**
 * Configuration for Sklik adapter registration
 */
export type SklikAdapterRegisterConfig =
	| {
			/** Sklik conversion id */
			conversionId: string;
			/** Event type this conversion id is for */
			eventType: SklikConversionEvent;
			retargetingId?: never;
	  }
	| {
			/** Sklik retargeting id */
			retargetingId: string;
			conversionId?: never;
			eventType?: never;
	  };

/**
 * Sklik conversion parameters
 */
export interface SklikConversionParams {
	/** Conversion value in the smallest currency unit (e.g. cents) */
	value: number;
	/** Currency code (e.g. CZK) */
	currency: string;
	/** Optional order id */
	orderId?: string;
	/** Optional transaction id */
	transactionId?: string;
	/** Consent from visitor for sending conversion hit */
	consent?: SklikConsentState;
}

/**
 * Sklik retargeting parameters
 */
export interface SklikRetargetingParams {
	/** Consent from visitor for sending retargeting hit */
	consent?: SklikConsentState;
	/** Optional item ID for product retargeting */
	itemId?: string;
	/** Optional category for category retargeting */
	category?: string;
	/** Optional page type (offerdetail or category) */
	pageType?: 'offerdetail' | 'category';
	/** Optional custom URL for retargeting */
	rtgUrl?: string;
}

/**
 * Sklik tracking adapter for conversion tracking
 * @author filipditrich <ditrich@nfctron.com>
 */
export class SklikAdapter {
	/** Whether tracking is allowed */
	private readonly allowTracking: boolean;
	private logger = createLogger('tracking', ['SklikAdapter']);

	/** Default consent value */
	private consentState: SklikConsentState = SklikConsentState.NO_CONSENT;

	/** Stored Sklik conversion ids by event type */
	public conversionIds: Record<SklikConversionEvent, string[]> = {
		purchase: [],
	};

	/** Stored Sklik retargeting ids */
	public retargetingIds: string[] = [];

	/** Sklik rc object */
	get rc(): typeof window.rc | undefined {
		if (typeof window === 'undefined') return undefined;
		return window.rc as typeof window.rc | undefined;
	}

	/**
	 * Determines if tracking is allowed
	 * Centralized logic to check if tracking should be performed
	 */
	get isTrackingAllowed(): boolean {
		return this.allowTracking && typeof window !== 'undefined';
	}

	/**
	 * Creates a new Sklik adapter instance
	 * @param allowTracking - Whether tracking is allowed
	 * @param initialConsent - Initial consent state (defaults to NO_CONSENT)
	 */
	constructor(allowTracking: boolean = false, initialConsent: SklikConsentState = SklikConsentState.NO_CONSENT) {
		this.allowTracking = allowTracking;
		this.consentState = initialConsent;
	}

	/**
	 * Updates the consent state
	 * @param consent - New consent state
	 */
	public updateConsent = (consent: boolean): void => {
		this.consentState = consent ? SklikConsentState.CONSENT : SklikConsentState.NO_CONSENT;
		this.logger.debug(`Consent state updated to: ${consent ? '✔︎ GRANTED' : '✘ DENIED'}`);
	};

	/**
	 * Gets the current consent state
	 * @returns Current consent state
	 */
	public getConsent = (): SklikConsentState => {
		return this.consentState;
	};

	/**
	 * Hashes an email address according to Sklik requirements
	 * 1. Remove whitespace
	 * 2. Convert to lowercase
	 * 3. Convert to UTF-8 encoded string
	 * 4. Hash with SHA-256
	 * 5. Convert to hexadecimal string
	 *
	 * @see https://napoveda.sklik.cz/merici-skripty/konverzni-kod/#konverzni-kod
	 * @param email - Email address to hash
	 * @returns Promise resolving to hashed email or null if hashing fails
	 */
	public hashEmail = async (email: string): Promise<string | null> => {
		if (!email || typeof email !== 'string') return null;

		try {
			/** remove whitespace and convert to lowercase */
			const normalizedEmail = email.trim().toLowerCase();

			/** convert to UTF-8 encoded array */
			const encoder = new TextEncoder();
			const data = encoder.encode(normalizedEmail);

			/** hash with SHA-256 */
			const hashBuffer = await crypto.subtle.digest('SHA-256', data);

			/** convert to hexadecimal string */
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

			return hashHex;
		} catch (error) {
			console.error('Failed to hash email:', error);
			return null;
		}
	};

	/**
	 * Updates user identity data for Sklik tracking
	 * @param identityData - User identity data
	 */
	public updateIdentities = async (identityData: SklikIdentityData): Promise<void> => {
		if (!this.isTrackingAllowed) return;

		try {
			/** create a copy of the identity data to avoid modifying the original */
			const processedData: SklikIdentityData = { ...identityData };

			/** hash the email if requested and email exists */
			if (!isEmpty(processedData.eid)) {
				const hashedEmail = await this.hashEmail(processedData.eid);
				if (hashedEmail) {
					processedData.eid = hashedEmail;
				}
			}

			if (window.sznIVA?.IS?.updateIdentities) {
				window.sznIVA.IS.updateIdentities(processedData);
				this.logger.debug('Updated user identity data', processedData);
			} else {
				this.logger.warn('window.sznIVA.IS.updateIdentities not available yet');
			}
		} catch (error) {
			this.logger.error('Failed to update Sklik identity data:', error);
		}
	};

	/**
	 * Registers a Sklik conversion id for a specific event type
	 * @param config - Sklik configuration
	 */
	public register = (config: SklikAdapterRegisterConfig, onLoad?: (it: this) => void): void => {
		/** ignore when tracking is disallowed */
		if (!this.isTrackingAllowed) return;

		try {
			/** ignore when duplicate conversionId is passed for this event type */
			if (config.conversionId && this.conversionIds[config.eventType].includes(config.conversionId)) return;
			if (config.retargetingId && this.retargetingIds.includes(config.retargetingId)) return;

			/** create script if it does not exist yet */
			const scriptId = `sklik-rc`;
			const scriptEl = document.getElementById(scriptId);

			if (!isDefined(scriptEl)) {
				/** construct the script */
				const script = document.createElement('script');
				script.id = scriptId;
				script.type = 'text/javascript';
				script.async = true;
				script.src = 'https://c.seznam.cz/js/rc.js';
				script.onload = () => {
					this.logger.debug('Sklik script loaded');
					onLoad?.(this);
				};

				/** insert the script to the top of <head> */
				document.head.insertBefore(script, document.head.firstChild);
			}

			/** register conversion id for this event type if provided */
			if (!isEmpty(config.conversionId) && !isEmpty(config.eventType)) {
				/** ignore when duplicate conversionId is passed for this event type */
				if (this.conversionIds[config.eventType].includes(config.conversionId)) return;

				this.conversionIds[config.eventType].push(config.conversionId);
				this.logger.debug(`Registered conversion ID ${config.conversionId} for event "${config.eventType}"`);
			}

			/** register retargeting id if provided */
			if (!isEmpty(config.retargetingId)) {
				/** ignore when duplicate retargetingId is passed */
				if (this.retargetingIds.includes(config.retargetingId)) return;

				this.retargetingIds.push(config.retargetingId);
				this.logger.debug(`Registered retargeting ID ${config.retargetingId}`);
			}
		} catch (error) {
			this.logger.error('Failed to register Sklik ID:', error);
		}
	};

	/**
	 * Tracks a conversion for a specific event type
	 * @param eventType - Event type to track
	 * @param params - Conversion parameters
	 * @param ref - Additional reference data
	 */
	public trackConversion = (eventType: SklikConversionEvent, params: SklikConversionParams, ref: Record<string, unknown> = {}): void => {
		/** ignore when tracking is disallowed */
		if (!this.isTrackingAllowed) return;
		/** or no conversion ids for this event type */
		if (this.conversionIds[eventType].length === 0) return;

		try {
			/** get all conversion ids for this event type */
			const conversionIds = this.conversionIds[eventType];

			/** track each conversion id */
			for (const convId of conversionIds) {
				/** create conversion configuration */
				const conversionConf = {
					id: convId,
					value: params.value / 100,
					consent: params.consent ?? this.consentState,
				};

				if (!isDefined(this.rc)) {
					this.logger.warn(`window.rc not available yet for ID ${convId}`);
					continue;
				}

				/** directly call conversion hit if available */
				this.rc.conversionHit(conversionConf);
				this.logger.debug(`Tracked conversion for ${eventType} with ID ${convId}`, conversionConf);
			}
		} catch (error) {
			this.logger.error(`Failed to track Sklik conversion for ${eventType}:`, error);
		}
	};

	/**
	 * Tracks a retargeting hit
	 * @param params - Retargeting parameters
	 */
	public trackRetargeting = (params: SklikRetargetingParams = {}): void => {
		/** ignore when tracking is disallowed */
		if (!this.isTrackingAllowed) return;
		/** or no retargeting ids */
		if (this.retargetingIds.length === 0) return;

		try {
			/** track each retargeting id */
			for (const rtgId of this.retargetingIds) {
				/** create retargeting configuration */
				const retargetingConf = {
					rtgId,
					itemId: params.itemId,
					category: params.category,
					pageType: params.pageType,
					rtgUrl: params.rtgUrl,
					consent: params.consent ?? this.consentState,
				} satisfies SklikRetargetingParams & { rtgId: string };

				if (!isDefined(this.rc)) {
					this.logger.warn(`window.rc not available yet for ID ${rtgId}`);
					continue;
				}

				this.rc.retargetingHit(retargetingConf);
				this.logger.debug(`Tracked retargeting with ID ${rtgId}`, retargetingConf);
			}
		} catch (error) {
			this.logger.error('Failed to track Sklik retargeting:', error);
		}
	};

	/**
	 * Purchase event handler
	 * @param params - Purchase parameters
	 * @param ref - Additional reference data
	 */
	public purchase = (params: SklikConversionParams, ref: Record<string, unknown> = {}): void => {
		this.trackConversion('purchase', params, ref);
	};

	/**
	 * Purchase success event handler
	 * @param params - Purchase success parameters
	 * @param ref - Additional reference data
	 */
	public purchaseSuccess = (params: SklikConversionParams, ref: Record<string, unknown> = {}): void => {};

	/**
	 * Purchase fail event handler - not supported by Sklik
	 * @param params - Purchase fail parameters
	 * @param ref - Additional reference data
	 */
	public purchaseFail = (params: SklikConversionParams, ref: Record<string, unknown> = {}): void => {};

	/**
	 * Initiate checkout event handler - not supported by Sklik
	 * @param params - Initiate checkout parameters
	 * @param ref - Additional reference data
	 */
	public initiateCheckout = (params: SklikConversionParams, ref: Record<string, unknown> = {}): void => {};

	/**
	 * Add to cart event handler - not supported by Sklik
	 * @param params - Add to cart parameters
	 * @param ref - Additional reference data
	 */
	public addToCart = (params: SklikConversionParams, ref: Record<string, unknown> = {}): void => {};

	/**
	 * Remove from cart event handler - not supported by Sklik
	 * @param params - Remove from cart parameters
	 * @param ref - Additional reference data
	 */
	public removeFromCart = (params: SklikConversionParams, ref: Record<string, unknown> = {}): void => {};

	/**
	 * Track page view for retargeting
	 * @param params - Optional retargeting parameters
	 */
	public handlePageView = (params: SklikRetargetingParams = {}): void => {
		this.logger.debug('Handling page view', params);
		this.trackRetargeting(params);
	};
}
